import { useEffect, useRef, useState } from "react";
import { Save, Building2, Loader2, Upload, X, Briefcase, GraduationCap, Clock, Lock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { DaycareSettingsCard } from "@/components/settings/DaycareSettingsCard";
import { AddressInput } from "@/components/AddressInput";
import { KioskAccessCard } from "@/components/settings/KioskAccessCard";
import { BadgeDesignerCard } from "@/components/settings/BadgeDesignerCard";
import { PhoneInput } from "@/components/PhoneInput";
import { SchoolSetup } from "@/components/settings/SchoolSetup";

const MAX_LOGO_BYTES = 500 * 1024; // 500 KB

export default function SettingsPage() {
  const [form, setForm] = useState({
    name: "", address: "", address_verified: false, phone: "", email: "", logo_data_url: "", brand_color: "",
    drop_off_start: "", drop_off_end: "",
    pickup_start: "", pickup_end: "", school_year_start: "08-01",
  });
  const [corporateLogo, setCorporateLogo] = useState("");
  const [inheritsCorporate, setInheritsCorporate] = useState(false);
  const [companyType, setCompanyType] = useState("business");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingType, setSavingType] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [b, c] = await Promise.all([
          api.get("/settings/building"),
          api.get("/me/company"),
        ]);
        setForm({
          name: b.data.name || "",
          address: b.data.address || "",
          address_verified: !!b.data.address_verified,
          phone: b.data.phone || "",
          email: b.data.email || "",
          logo_data_url: b.data.logo_data_url || "",   // location's OWN logo only
          brand_color: b.data.brand_color || "",
          drop_off_start: b.data.drop_off_start || "",
          drop_off_end: b.data.drop_off_end || "",
          pickup_start: b.data.pickup_start || "",
          pickup_end: b.data.pickup_end || "",
          school_year_start: b.data.school_year_start || "08-01",
        });
        setCorporateLogo(b.data.corporate_logo_data_url || "");
        setInheritsCorporate(!!b.data.inherits_corporate_logo);
        setCompanyType(c.data.type || "business");
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load settings");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/settings/building", form);
      setForm({
        name: data.name,
        address: data.address,
        address_verified: !!data.address_verified,
        phone: data.phone,
        email: data.email || "",
        logo_data_url: data.logo_data_url || "",
        brand_color: data.brand_color || "",
        drop_off_start: data.drop_off_start || "",
        drop_off_end: data.drop_off_end || "",
        pickup_start: data.pickup_start || "",
        pickup_end: data.pickup_end || "",
        school_year_start: data.school_year_start || "08-01",
      });
      setCorporateLogo(data.corporate_logo_data_url || "");
      setInheritsCorporate(!!data.inherits_corporate_logo);
      // Tell AdminLayout (and any other listener) to refresh its sidebar brand + logo.
      window.dispatchEvent(new CustomEvent("logerd:building-updated"));
      toast.success("Building info saved — exports will use these details from now on.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, SVG).");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error(`Logo must be under ${(MAX_LOGO_BYTES / 1024).toFixed(0)} KB.`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setForm((f) => ({ ...f, logo_data_url: String(reader.result) }));
      toast.success("Logo loaded — click Save to apply.");
    };
    reader.onerror = () => toast.error("Failed to read file.");
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    setForm((f) => ({ ...f, logo_data_url: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="p-6 md:p-10" data-testid="settings-page">
      <header className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-slate-500">Configuration</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Settings</h1>
        <p className="text-sm text-slate-600 mt-1">
          Building details shown on every CSV / PDF report and on the kiosk welcome screen{companyType === "school" ? ", plus your school's academic setup" : ""}.
        </p>
      </header>

      {companyType === "school" && <div className="mb-6"><SchoolSetup /></div>}
      {companyType === "daycare" && <div className="mb-6"><DaycareSettingsCard /></div>}

      <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <section className="rounded-xl border border-slate-200 bg-white p-6 space-y-5">
          <Field label="Building type" hint="Schools get a parent pickup kiosk + student roster. Businesses get the visitor/contractor kiosk.">
            <div className="flex items-center gap-3 h-14 rounded-lg border-2 border-primary bg-primary/5 px-4" data-testid="settings-type-display">
              {companyType === "school" || companyType === "daycare"
                ? <GraduationCap className="h-5 w-5 text-primary" />
                : <Briefcase className="h-5 w-5 text-primary" />}
              <span className="font-display text-base font-semibold text-slate-900">
                {{ school: "School", daycare: "Daycare / Pre-K", care: "Personal Care Home / Adult Care" }[companyType] || "Office / Business"}
              </span>
              <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                <Lock className="h-3 w-3" /> Locked — only the software owner can change the building type
              </span>
            </div>
          </Field>
          <Field label="Logo (optional)" hint={inheritsCorporate ? "This location currently inherits the corporate logo. Upload here to override it for this location only." : "PNG, JPG or SVG · max 500 KB. Appears on the PDF report and the kiosk."}>
            <div className="flex items-center gap-4">
              <div
                className="flex h-20 w-20 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 overflow-hidden relative"
                data-testid="settings-logo-preview"
              >
                {form.logo_data_url ? (
                  <img src={form.logo_data_url} alt="Logo" className="max-h-full max-w-full object-contain" />
                ) : inheritsCorporate && corporateLogo ? (
                  <>
                    <img src={corporateLogo} alt="Corporate logo" className="max-h-full max-w-full object-contain opacity-70" />
                    <span className="absolute bottom-1 left-1 right-1 text-[9px] uppercase tracking-widest text-slate-500 text-center bg-white/70 rounded">Inherited</span>
                  </>
                ) : (
                  <Building2 className="h-6 w-6 text-slate-400" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/svg+xml,image/webp"
                  onChange={onLogoFile}
                  className="hidden"
                  data-testid="settings-logo-file"
                  id="settings-logo-file"
                  disabled={loading}
                />
                <label
                  htmlFor="settings-logo-file"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 cursor-pointer transition"
                >
                  <Upload className="h-4 w-4" /> {form.logo_data_url ? "Replace logo" : (inheritsCorporate ? "Override with location logo" : "Upload logo")}
                </label>
                {form.logo_data_url && (
                  <button
                    type="button"
                    onClick={clearLogo}
                    data-testid="settings-logo-clear"
                    className="inline-flex items-center gap-2 text-xs text-destructive hover:underline"
                  >
                    <X className="h-3 w-3" /> {inheritsCorporate || corporateLogo ? "Remove · go back to corporate logo" : "Remove logo"}
                  </button>
                )}
              </div>
            </div>
          </Field>

          <Field label="Building name" hint="Shown on every report and on the kiosk header.">
            <input
              required
              value={form.name}
              onChange={set("name")}
              data-testid="settings-name"
              maxLength={160}
              className={inputCls}
              placeholder="Acme Headquarters / Lincoln Elementary"
              disabled={loading}
            />
          </Field>

          <Field label="Brand color" hint="Header band color on invoices and receipts. Leave on default for Logerd navy.">
            <div className="flex items-center gap-3">
              <input type="color" value={form.brand_color || "#0f4c81"} onChange={(e) => setForm((f) => ({ ...f, brand_color: e.target.value }))} className="h-9 w-12 cursor-pointer rounded-lg border border-slate-200 bg-white p-1" data-testid="settings-brand-color" disabled={loading} />
              <span className="font-mono text-xs text-slate-600">{form.brand_color || "#0f4c81"}</span>
              {form.brand_color && <button type="button" onClick={() => setForm((f) => ({ ...f, brand_color: "" }))} className="text-xs text-slate-500 hover:underline" data-testid="settings-brand-color-reset">Reset</button>}
            </div>
          </Field>

          <Field label="Address" hint="Start typing and pick the Google suggestion to verify the address.">
            <AddressInput
              value={form.address}
              verified={form.address_verified}
              onChange={(v, ok) => setForm({ ...form, address: v, address_verified: ok })}
              testid="settings-address"
              className={inputCls}
              required
              placeholder="123 Main Street, Dallas, TX 75201"
              disabled={loading}
            />
          </Field>

          <Field label="Phone (optional)" hint="Reception desk or facilities contact.">
            <PhoneInput
              value={form.phone}
              onChange={(v) => setForm((f) => ({ ...f, phone: v }))}
              data-testid="settings-phone"
              maxLength={40}
              className={inputCls}
              placeholder="(214) 555-0100"
              disabled={loading}
            />
          </Field>

          <Field label="Reply-to email (optional)" hint="Parents and staff who reply to automated emails (alerts, letters, invites, forms) reach this inbox. Leave blank for no-reply.">
            <input
              type="email" value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              data-testid="settings-reply-email" maxLength={120} className={inputCls}
              placeholder="office@yourschool.org" disabled={loading}
            />
          </Field>

          {(companyType === "school" || companyType === "daycare") && (
            <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 -mx-1" data-testid="school-schedule-card">
              <div className="flex items-center gap-2 mb-1.5">
                <Clock className="h-4 w-4 text-primary" />
                <span className="text-xs font-medium uppercase tracking-widest text-slate-500">School schedule</span>
              </div>
              <p className="text-xs text-slate-500 mb-4">
                During the drop-off window only the <span className="font-semibold">Drop Off</span> button is shown. During pickup only <span className="font-semibold">Pickup</span> is shown. Outside both windows, parents see <span className="font-semibold">Tardy</span> and <span className="font-semibold">Early Dismissal</span>. Leave a field blank to always show that action.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Drop-off start" hint="">
                  <input type="time" value={form.drop_off_start} onChange={set("drop_off_start")} data-testid="settings-drop-off-start" className={inputCls} disabled={loading} />
                </Field>
                <Field label="Drop-off end" hint="">
                  <input type="time" value={form.drop_off_end} onChange={set("drop_off_end")} data-testid="settings-drop-off-end" className={inputCls} disabled={loading} />
                </Field>
                <Field label="Pickup start" hint="">
                  <input type="time" value={form.pickup_start} onChange={set("pickup_start")} data-testid="settings-pickup-start" className={inputCls} disabled={loading} />
                </Field>
                <Field label="Pickup end" hint="">
                  <input type="time" value={form.pickup_end} onChange={set("pickup_end")} data-testid="settings-pickup-end" className={inputCls} disabled={loading} />
                </Field>
              </div>
              {companyType === "school" && (
                <div className="mt-4 max-w-xs">
                  <Field label="School year starts" hint="Every year on this day all car-rider numbers are re-issued from 1 (by grade, then name). New students get the next free number automatically.">
                    <div className="flex items-center gap-2">
                      <select value={form.school_year_start.split("-")[0]} onChange={(e) => setForm((f) => ({ ...f, school_year_start: `${e.target.value}-${f.school_year_start.split("-")[1] || "01"}` }))} className={inputCls} data-testid="settings-school-year-month" disabled={loading}>
                        {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => <option key={m} value={String(i + 1).padStart(2, "0")}>{m}</option>)}
                      </select>
                      <input type="number" min="1" max="31" value={Number(form.school_year_start.split("-")[1] || 1)} onChange={(e) => setForm((f) => ({ ...f, school_year_start: `${f.school_year_start.split("-")[0] || "08"}-${String(Math.min(31, Math.max(1, Number(e.target.value) || 1))).padStart(2, "0")}` }))} className={`${inputCls} w-20`} data-testid="settings-school-year-day" disabled={loading} />
                    </div>
                  </Field>
                </div>
              )}
            </div>
          )}

          <div className="pt-3">
            <button
              type="submit"
              disabled={saving || loading}
              data-testid="settings-save-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-white hover:shadow disabled:opacity-60 transition"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save changes
            </button>
          </div>
        </section>

        {/* Live preview */}
        <aside
          className="rounded-xl border border-slate-200 bg-slate-50 p-5"
          data-testid="settings-preview"
        >
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Report header preview</div>
          <div className="rounded-lg bg-white border border-slate-200 p-5">
            <div className="flex items-center gap-2 text-primary text-xs uppercase tracking-widest mb-3">
              <Building2 className="h-3.5 w-3.5" /> Logerd
            </div>
            <div className="flex items-start gap-3">
              {form.logo_data_url && (
                <img
                  src={form.logo_data_url}
                  alt="Logo"
                  className="h-12 w-12 object-contain shrink-0"
                />
              )}
              <div>
                <div className="font-display text-lg font-bold text-slate-900">
                  {form.name || <span className="text-slate-400">Building name…</span>}
                </div>
                <div className="mt-1 text-sm text-slate-700 whitespace-pre-line">
                  {form.address || <span className="text-slate-400">Address…</span>}
                </div>
                {form.phone && (
                  <div className="mt-1 text-sm text-slate-700">Tel: {form.phone}</div>
                )}
                {form.email && (
                  <div className="text-sm text-slate-700">{form.email}</div>
                )}
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] text-slate-400">
              Generated · {new Date().toLocaleString()}
            </div>
          </div>
        </aside>
      </form>
      <div className="mt-6 max-w-3xl">
        <KioskAccessCard />
      </div>
      <div className="mt-6 max-w-5xl">
        <BadgeDesignerCard />
      </div>
    </div>
  );
}

const inputCls =
  "w-full h-11 rounded-lg border border-slate-200 bg-white px-3 text-sm font-body text-slate-900 placeholder:text-slate-400 focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none transition";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">{label}</span>
      {children}
      {hint && <span className="block mt-1.5 text-xs text-slate-500">{hint}</span>}
    </label>
  );
}
